import AdminOnly from './AdminOnly';
import {useSpeculativeTyping} from '../hooks/useSpeculativeTyping';
import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useAuth } from './AuthProvider';
import api from '../utils/api';
import { connectToWebSocket, sendMessage as wsSendMessage, sendRaw, addMessageHandler, removeMessageHandler, disconnect, isConnected, getConnectionInfo, getHandlerCount, clearAllHandlers } from '../utils/websocket';
import { debugWS, warnWS, errorWS, logWSLifecycle, logWSMessage } from '../utils/debugWS';
import { WS_BACKEND_URL, BACKEND_URL } from '../config';
import InviteButton from './InviteButton';
import { useTypingIndicators } from '../hooks/useTypingIndicators';
import StoryRoomEditor from './StoryRoomEditor';
import './TypingIndicator.css';

// Helper function to extract display name from user object
const getDisplayNameFromUser = (user) => {
  if (!user) return 'Guest';
  
  // Try different user object properties
  return (
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    user.user_metadata?.username ||
    user.name ||
    user.username ||
    user.email?.split('@')[0]?.replace('.', ' ').replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') ||
    'Guest'
  );
};

const ChatRoom = ({ room, user, onLeave, onSignIn }) => {
  // Guard: Ensure required room parameter exists
  if (!room || !room.id) {
    console.warn('ChatRoom: Missing required room parameter');
    return null;
  }

  // CRITICAL: Get user display name FIRST, before any other logic that might reference it
  const userDisplayName = useMemo(() => {
    // When in a guest room, always use the guest identity from sessionStorage,
    // even if the user is also logged in (e.g. joining a Daily invite while authenticated).
    if (room.isGuest) {
      const storedGuestUser = sessionStorage.getItem('guest_user');
      if (storedGuestUser) {
        try {
          const guestUser = JSON.parse(storedGuestUser);
          console.log('🎭 USER DISPLAY NAME: Using guest display name:', guestUser.display_name);
          return guestUser.display_name;
        } catch (error) {
          console.warn('Error parsing guest user data for display name:', error);
          return 'Guest';
        }
      }
      return 'Guest';
    }
    if (user) {
      // Authenticated user - use the existing function
      return getDisplayNameFromUser(user);
    }
    return 'Guest';
  }, [user, room.isGuest]);

  const [messages, setMessages] = useState([]);
  
  // Debug: Log when messages state changes - TEMPORARY
  useEffect(() => {
    console.log('🔄 MESSAGES STATE CHANGED - New count:', messages.length);
    if (messages.length > 0) {
      console.log('🔄 LAST MESSAGE:', { 
        id: messages[messages.length - 1]?.id, 
        content: messages[messages.length - 1]?.content?.substring(0, 30) 
      });
    }
  }, [messages]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [sceneInfo, setSceneInfo] = useState(null);
  const [sceneLoading, setSceneLoading] = useState(false);
  const [showStoryLog, setShowStoryLog] = useState(false);
  const [showRoomEditor, setShowRoomEditor] = useState(false);
  const [storyLogs, setStoryLogs] = useState([]);
  const [storyLogError, setStoryLogError] = useState(null);
  const [logFilter, setLogFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const logStreamRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const debugIdRef = useRef(Date.now()); // Unique debug session ID
  const { session } = useAuth();
  const seenMessagesRef = useRef(new Set()); // Track seen message IDs
  const handlerRegisteredRef = useRef(false); // Prevent duplicate handler registration
  const isMountedRef = useRef(true); // Track if component is mounted

  // Typing indicators state (managed internally)
  const [typingActiveCount, setTypingActiveCount] = useState(0);
  const [typingIsAnyoneTyping, setTypingIsAnyoneTyping] = useState(false);
  const [sceneTransitioning, setSceneTransitioning] = useState(false);
  const [responseDraftsAvailable, setResponseDraftsAvailable] = useState(false);
  const {enabled: responseDraftsEnabled,
    setEnabled: setResponseDraftsEnabled, beforeSubmit: stopDraftTimer} =
    useSpeculativeTyping(room?.id, newMessage, connected && !sceneTransitioning, responseDraftsAvailable);

  
  // Typing indicators integration
  const {
    isCurrentUserTyping,
    handleKeyDown: handleTypingKeyDown,
    handleMessageSend: handleTypingMessageSend,
    sendTypingStop
  } = useTypingIndicators(room?.id);

  // Memoized debug logger to prevent recreation - TEMPORARILY DISABLED
  const debugLog = useCallback((message, data = null) => {
    // Temporarily disabled to fix initialization issue
    // const timestamp = new Date().toISOString().substr(11, 12);
    // const sessionId = debugIdRef.current.toString().substr(-4);
    // const prefix = `🐛 [${timestamp}][${sessionId}]`;
    
    // if (data) {
    //   console.log(`${prefix} ${message}`, data);
    // } else {
    //   console.log(`${prefix} ${message}`);
    // }
  }, []); // No dependencies - debugIdRef is stable

  // Track prop changes that cause re-renders - FIXED: Added proper dependencies
  const prevRoomRef = useRef();
  const prevUserRef = useRef();
  const prevOnLeaveRef = useRef();
  
  // Initialize component with debug logging  
  useEffect(() => {
    // debugLog('🚀 ChatRoom component initialized', { 
    //   roomId: room?.id, 
    //   userId: user?.id, 
    //   hasSession: !!session?.access_token,
    //   roomObject: typeof room,
    //   userObject: typeof user,
    //   sessionObject: typeof session,
    //   roomReference: room === room ? 'stable' : 'changed',
    //   userReference: user === user ? 'stable' : 'changed'
    // });
  }, []); // Remove debugLog dependency
  
  useEffect(() => {
    if (prevRoomRef.current !== room) {
      // debugLog('🔄 Room prop changed', { 
      //   oldRoom: prevRoomRef.current?.id, 
      //   newRoom: room?.id,
      //   roomChanged: prevRoomRef.current !== room
      // });
      prevRoomRef.current = room;
    }
    
    if (prevUserRef.current !== user) {
      // debugLog('🔄 User prop changed', { 
      //   oldUser: prevUserRef.current?.id, 
      //   newUser: user?.id,
      //   userChanged: prevUserRef.current !== user
      // });
      prevUserRef.current = user;
    }
    
    if (prevOnLeaveRef.current !== onLeave) {
      // debugLog('🔄 OnLeave prop changed', { 
      //   onLeaveChanged: prevOnLeaveRef.current !== onLeave 
      // });
      prevOnLeaveRef.current = onLeave;
    }
  }, [room, user, onLeave]); // Remove debugLog dependency

  // Auto-scroll to bottom when messages change or on initialization
  useEffect(() => {
    // debugLog('📜 Messages changed, scrolling to bottom', { 
    //   messageCount: messages.length,
    //   hasMessages: messages.length > 0 
    // });
    
    if (messagesEndRef.current) {
      // Use smooth scrolling for better UX
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'end' 
      });
      // debugLog('✅ Scrolled to bottom successfully');
    } else {
      // debugLog('⚠️ messagesEndRef not available for scrolling');
    }
  }, [messages]); // Remove debugLog dependency

  // Track component mounting/unmounting
  useEffect(() => {
    // debugLog('🚨 ChatRoom mounted once - component lifecycle start');
    return () => {
      // debugLog('💀 ChatRoom unmounting - component lifecycle end');
      isMountedRef.current = false;
    };
  }, []); // Remove debugLog dependency




  // Track initial connection attempt to prevent showing "Disconnected" error on first load
  const initialConnectionRef = useRef(true);

  // Memoized WebSocket message handler to prevent recreation - FIXED DEPENDENCIES
  // C-proto: data is typed as ServerWSEvent (see frontend/src/proto/interfaces.ts)
  const handleWebSocketMessage = useCallback(/** @param {import('../proto/interfaces').ServerWSEvent} data */ (data) => {
    logWSMessage('📨 CHATROOM HANDLER - FULL DATA STRUCTURE:');
    logWSMessage(JSON.stringify(data, null, 2));
    
    if (data.type === 'new_message') {
      const messageId = data.id;
      
      logWSMessage('📨 NEW_MESSAGE DATA:');
      logWSMessage(JSON.stringify(data, null, 2));
      logWSMessage('📨 Message ID:', data.id);
      logWSMessage('📨 Content:', data.content);
      logWSMessage('📨 Sender:', data.sender_name);
      logWSMessage('📨 Message Type:', data.message_type);
      logWSMessage('📨 Sender Type:', data.sender_type);
      
      // SCENE TRANSITION DETECTION: Check if this is a scene transition from narrator
      if (data.sender_name === 'Narrator' && data.metadata) {
        logWSMessage('📨 SCENE TRANSITION CHECK: Checking narrator message for scene transition');
        
        // Check for scene transition metadata
        if (data.metadata.update_scene_indicator && data.metadata.scene_sequence) {
          logWSMessage('📨 SCENE TRANSITION DETECTED: Updating scene indicator');
          logWSMessage(`📨 Scene: ${sceneInfo?.sequence || 'unknown'} → ${data.metadata.scene_sequence}`);

          // Re-fetch full scene info (including activeCharacterIds) from the backend.
          // Small delay gives the DB write a moment to complete before we read it back.
          setTimeout(() => loadSceneInfo(), 500);

          console.log('✅ Scene indicator updated to:', data.metadata.scene_sequence);
        }
      }
      
      // Enhanced duplicate detection: Check for missing ID (common in echo messages)
      if (!messageId || messageId === undefined || messageId === null) {
        logWSMessage('📨 SKIPPING MESSAGE WITH MISSING/INVALID ID:', messageId);
        return;
      }
      
      // ANTI-ECHO PROTECTION: Don't process messages from ourselves
      const isFromCurrentUser = userDisplayName && data.sender_name === userDisplayName;
      
      // For guest users, check against stored guest user data
      let isFromCurrentGuestUser = false;
      if (!user || room.isGuest) {
        const storedGuestUser = sessionStorage.getItem('guest_user');
        if (storedGuestUser) {
          try {
            const guestUser = JSON.parse(storedGuestUser);
            
            // FIXED GUEST ECHO DETECTION: Use exact matching only, no patterns
            // Frontend stores names like "Guest 9766", backend should match exactly
            
            // Method 1: Exact match (primary)
            const exactMatch = data.sender_name === guestUser.display_name;
            
            // Method 2: Check user ID if available in WebSocket data (fallback)
            const userIdMatch = data.user_id && guestUser.id && (
              data.user_id === guestUser.id || 
              data.user_id.includes(guestUser.id) || 
              guestUser.id.includes(data.user_id)
            );
            
            isFromCurrentGuestUser = exactMatch || userIdMatch;
            
            logWSMessage('📨 FIXED GUEST ECHO DEBUG: Checking WebSocket message sender');
            logWSMessage('   Message sender:', data.sender_name);
            logWSMessage('   Guest display name:', guestUser.display_name);
            logWSMessage('   Guest ID:', guestUser.id);
            logWSMessage('   Message user ID:', data.user_id);
            logWSMessage('   Exact match:', exactMatch);
            logWSMessage('   User ID match:', userIdMatch);
            logWSMessage('   Final result - Is from current guest user:', isFromCurrentGuestUser);
          } catch (error) {
            logWSMessage('📨 Error parsing guest user data for echo detection:', error);
          }
        }
      }
      
      // Check if this is an AI message, system message, or narrator message (should never be filtered)
      const isAIMessage = data.message_type === 'ai' || data.sender_type === 'ai_response' || data.sender_type === 'ai';
      const isSystemMessage = data.message_type === 'system' || data.sender_type === 'system';
      const isNarratorMessage = data.message_type === 'narrator' || data.sender_type === 'narrator';
      const shouldAlwaysDisplay = isAIMessage || isSystemMessage || isNarratorMessage;
      
      // REORDERED LOGIC: Check for optimistic message updates BEFORE echo filtering
      
      // For guest users: Check if this broadcast message corresponds to an optimistic message
      let optimisticMessageUpdated = false;
      if ((!user || room.isGuest) && (isFromCurrentUser || isFromCurrentGuestUser)) {
        console.log('🔄 GUEST ECHO: Checking for optimistic update - Sender:', data.sender_name, 'Content:', data.content);
        console.log('🔄 CHECKING: Current messages state...');
        
        // Try to update optimistic message with server data
        setMessages(prev => {
          const optimisticMsgs = prev.filter(msg => msg.isOptimistic);
          console.log('🔄 FOUND:', optimisticMsgs.length, 'optimistic messages');
          
          const optimisticIndex = prev.findIndex(msg => 
            msg.isOptimistic && msg.content?.trim() === data.content?.trim()
          );
          
          if (optimisticIndex !== -1) {
            console.log('✅ UPDATING OPTIMISTIC:', prev[optimisticIndex].sender_name, '→', data.sender_name);
            
            const updatedMessages = [...prev];
            updatedMessages[optimisticIndex] = {
              ...prev[optimisticIndex],
              id: data.id,
              sender_name: data.sender_name, // Sync display name!
              created_at: data.created_at,
              isOptimistic: false
            };
            
            optimisticMessageUpdated = true;
            return updatedMessages;
          } else {
            console.log('❌ NO OPTIMISTIC MATCH. Available optimistic messages:', optimisticMsgs.length);
            optimisticMsgs.forEach(msg => console.log(`   "${msg.content}" (${msg.sender_name})`));
            console.log('   Looking for broadcast:', `"${data.content}"`);
          }
          
          return prev;
        });
      }
      
      // Echo filtering - skip if we updated optimistic message
      if ((isFromCurrentUser || isFromCurrentGuestUser) && !shouldAlwaysDisplay) {
        if (optimisticMessageUpdated) {
          console.log('✅ OPTIMISTIC UPDATED - Processing complete');
          return;
        } else {
          console.log('🚫 ECHO FILTERED:', data.sender_name);
          return;
        }
      }
      
      if (isAIMessage || isSystemMessage || isNarratorMessage) {
        logWSMessage('📨 PROCESSING ALWAYS-DISPLAY MESSAGE:', data.sender_name);
        logWSMessage('📨 Message type:', data.message_type);
        logWSMessage('📨 Sender type:', data.sender_type);
        logWSMessage('📨 Is AI message:', isAIMessage);
        logWSMessage('📨 Is system message:', isSystemMessage);
        logWSMessage('📨 Is narrator message:', isNarratorMessage);
        
        // Special logging for narrator messages
        if (isNarratorMessage) {
          logWSMessage('🎭 NARRATOR MESSAGE DETECTED:', data.content.substring(0, 100) + '...');
        }
      }
      
      // Check if we've already seen this message (using multiple identifiers)
      const messageSignature = `${messageId}-${data.content}-${data.sender_name}-${data.created_at}`;
      const contentSignature = `${data.content}-${data.sender_name}`;
      
      if (seenMessagesRef.current.has(messageId) || 
          seenMessagesRef.current.has(messageSignature) ||
          seenMessagesRef.current.has(contentSignature)) {
        logWSMessage('📨 Skipping duplicate message:', messageId);
        return;
      }
      
      // Add message to state first - data is the message object directly
      setMessages(prev => {
        // Enhanced duplicate checking with multiple criteria
        const messageExists = prev.some(msg => {
          // Check by ID first
          if (msg.id === data.id && data.id) return true;
          
          // Check by content + sender for messages without proper IDs
          if (msg.content === data.content && 
              msg.sender_name === data.sender_name &&
              Math.abs(new Date(msg.created_at) - new Date(data.created_at)) < 5000) {
            return true;
          }
          
          return false;
        });
        
        if (messageExists) {
          logWSMessage('📨 MESSAGE ALREADY EXISTS, skipping duplicate:', data.id);
          return prev; // Return previous state unchanged
        }

        // Remove any optimistic messages that match this real message
        const filteredMessages = prev.filter(msg => {
          if (msg.isOptimistic && 
              msg.content === data.content && 
              msg.sender_name === data.sender_name) {
            logWSMessage('📨 REMOVING OPTIMISTIC MESSAGE replaced by real message:', msg.id);
            return false;
          }
          return true;
        });

        const newMessage = {
          id: data.id,
          content: data.content,
          sender_name: data.sender_name,
          message_type: data.message_type,
          created_at: data.created_at,
          room_id: data.room_id
        };

        logWSMessage('📨 CREATING NEW MESSAGE OBJECT:');
        logWSMessage(JSON.stringify(newMessage, null, 2));
        logWSMessage('📨 PREVIOUS MESSAGE COUNT:', prev.length);

        const newMessages = [...filteredMessages, newMessage];
        logWSMessage('📨 NEW MESSAGES ARRAY LENGTH:', newMessages.length);
        logWSMessage('📨 STATE UPDATE - Adding message with ID:', data.id);
        logWSMessage('📨 REACT STATE UPDATE - Returning new array reference');
        return newMessages;
      });
      
      // Track the latest message timestamp so we can request missed messages on reconnect
      if (data.created_at) {
        localStorage.setItem(`playscreen_last_msg_ts_${room.id}`, data.created_at);
      }

      // Mark as seen AFTER successful state update - ONLY for user messages
      // AI messages should NOT be cached so they can always display in real-time
      const isUserMessage = data.message_type === 'user' || data.sender_type === 'user';
      
      if (isUserMessage) {
        if (messageId) {
          seenMessagesRef.current.add(messageId);
        }
        seenMessagesRef.current.add(messageSignature);
        seenMessagesRef.current.add(contentSignature);
        logWSMessage('📨 CACHED USER MESSAGE for echo prevention:', messageId);
      } else {
        logWSMessage('📨 SKIPPED CACHING AI MESSAGE (allows real-time display):', messageId);
      }
      
    } else if (data.type === 'connection_status') {
      logWSMessage('📨 CONNECTION STATUS DATA:');
      logWSMessage(JSON.stringify(data, null, 2));
      setConnected(data.connected);
      
      // Only show disconnection error after initial connection has been established
      if (data.connected) {
        initialConnectionRef.current = false; // Mark that we've had a successful connection
        setError(''); // Clear any errors on successful connection
      } else if (!initialConnectionRef.current) {
        // Only show disconnection error if we were previously connected
        setError('Disconnected');
      }
      // If this is the initial connection attempt and it fails, don't show error yet
      
    } else if (data.type === 'connection_error') {
      logWSMessage('📨 CONNECTION ERROR DATA:');
      logWSMessage(JSON.stringify(data, null, 2));
      
      // Only show connection errors after initial connection attempt
      if (!initialConnectionRef.current) {
        setError(data.error || 'Connection error');
      }
      setConnected(false);
    } else if (data.type === 'connection_confirmed') {
      setResponseDraftsAvailable(data.response_drafts_enabled === true);
      logWSMessage('📨 CONNECTION CONFIRMED:');
      logWSMessage(JSON.stringify(data, null, 2));
      setConnected(true);
      initialConnectionRef.current = false; // Mark that we've had a successful connection
      setError(''); // Clear any errors on successful connection
    } else if (data.type === 'reconnected') {
      // WebSocket reconnected after a drop — request any messages missed during the gap
      const lastTs = localStorage.getItem(`playscreen_last_msg_ts_${room.id}`);
      if (lastTs) {
        logWSLifecycle(`🔄 Reconnected — requesting missed messages since ${lastTs}`);
        sendRaw({ type: 'sync_missed_messages', since_timestamp: lastTs, room_id: room.id });
      }
    } else if (data.type === 'error') {
      setError(data.message || 'Story generation is temporarily unavailable.');
      setTypingActiveCount(0);
      setTypingIsAnyoneTyping(false);
      setSceneTransitioning(false);
    } else if (data.type === 'turn_dropped') {
      setMessages(prev => [...prev, {
        id: `turn_dropped_${Date.now()}`,
        sender_name: 'Narrator',
        sender_type: 'narrator',
        message_type: 'message',
        content: '[Sorry, an error occurred. Please resend your message and the story will continue.]',
        created_at: new Date().toISOString(),
        timestamp: new Date().toISOString(),
      }]);
    } else if (data.type === 'typing_status') {
      // Handle typing status directly in main handler
      debugWS(`⌨️ TYPING STATUS: 📥 Received for room ${room?.id}:`, data);
      console.log(`⌨️ TYPING STATUS RECEIVED: ${data.active_count} users typing in room ${room?.id}`, data);
      
      if (data.room_id === room?.id) {
        const count = data.active_count || 0;
        const isTyping = data.is_typing || false;
        
        setTypingActiveCount(count);
        setTypingIsAnyoneTyping(isTyping);
        
        debugWS(`⌨️ TYPING STATUS: Updated state - count=${count}, isTyping=${isTyping}`);
        
        if (count > 0) {
          console.log(`⌨️ TYPING INDICATOR SHOULD BE VISIBLE: "${data.message}" with count ${count}`);
        } else {
          console.log(`⌨️ TYPING INDICATOR SHOULD BE HIDDEN: count is 0`);
        }
      } else {
        console.log(`⌨️ TYPING STATUS: Ignoring for different room - data: ${data.room_id}, current: ${room?.id}`);
      }
    } else if (data.type === 'narrator_transitioning') {
      setSceneTransitioning(data.active === true);
    } else {
      logWSMessage('📨 UNKNOWN MESSAGE TYPE:');
      logWSMessage(JSON.stringify(data, null, 2));
    }
  }, [setMessages, setConnected, setError, user, userDisplayName, sceneInfo, setSceneInfo, setSceneTransitioning]); // Add sceneInfo dependencies for scene transition updates

  // Memoized loadMessages function to prevent recreation - MOVED UP to fix dependency ordering
  const loadMessages = useCallback(async () => {
    // debugLog('📥 Starting to load messages', { roomId: room?.id });
    try {
      setLoading(true);
      setError(''); // Clear any existing errors when starting to load messages
      
      // Request 200 messages for better story context (backend default)
      let messagesUrl = `/messages/${room.id}?limit=200`;
      try {
        const stored = sessionStorage.getItem('guest_user');
        if (stored) {
          const guestUser = JSON.parse(stored);
          if (guestUser?.id) messagesUrl += `&guest_id=${guestUser.id}`;
        }
      } catch (_) {}
      const response = await api.get(messagesUrl);
      
      // debugLog('📥 Messages loaded successfully', { 
      //   count: response.messages?.length || 0,
      //   roomId: room.id,
      //   responseStructure: Object.keys(response),
      //   requestedLimit: 200
      // });
      
      // Use response.messages instead of response.data
      if (!response.messages || response.messages.length === 0) {
        // Don't wipe WebSocket messages that arrived before this response did.
        // On a new room, loadMessages fires before the welcome is stored; the
        // welcome arrives via WebSocket and gets added to state. If we then
        // setMessages([]) we erase it. Only clear if state is already empty.
        setMessages(prev => prev.length > 0 ? prev : []);
      } else {
        setMessages(response.messages);
        
        // Pre-populate seen messages cache ONLY with user messages to prevent echoes
        // AI messages should NOT be cached so they can display in real-time via WebSocket
        seenMessagesRef.current.clear();
        response.messages.forEach(msg => {
          // Only cache user messages for echo prevention
          if (msg.message_type === 'user' || msg.sender_type === 'user') {
            seenMessagesRef.current.add(msg.id);
            // Also add content signatures to prevent optimistic duplicates
            const contentSignature = `${msg.content}-${msg.sender_name}`;
            seenMessagesRef.current.add(contentSignature);
          }
          // Skip caching AI messages - they should always display when received via WebSocket
        });
        // debugLog('📥 Pre-populated seen messages cache', { 
        //   cacheSize: seenMessagesRef.current.size 
        // });
      }
    } catch (error) {
      // debugLog('❌ Error loading messages', { error: error.message, roomId: room?.id });
      console.error('Error loading messages:', error);
      setMessages([]); // Set empty array on error
      seenMessagesRef.current.clear(); // Clear cache on error
      // Only show error for message loading failures, not initial connection issues
      if (error.message && !error.message.includes('Network Error')) {
        setError('Failed to load message history');
      }
    } finally {
      setLoading(false);
      // debugLog('📥 Load messages completed', { roomId: room?.id });
    }
  }, [room?.id]); // Remove debugLog dependency

  // Memoized loadSceneInfo function to get current scene state
  const loadSceneInfo = useCallback(async () => {
    if (!room?.id) return;
    
    // Start a timer - only show loading if this takes longer than 500ms
    let showLoadingTimer = null;
    let hasSetLoading = false;
    
    try {
      const setLoadingAfterDelay = () => {
        showLoadingTimer = setTimeout(() => {
          setSceneLoading(true);
          hasSetLoading = true;
        }, 500); // Only show loading if API call takes more than 500ms
      };
      
      setLoadingAfterDelay();
      console.log('🎬 Loading scene info for room:', room.id);
      
      // Call the scene validation endpoint to get current scene info
      const response = await api.get(`/scenes/validate/${room.id}`);
      
      // Clear the loading timer since we got a response
      if (showLoadingTimer) {
        clearTimeout(showLoadingTimer);
      }
      
      if (response.ready && response.scene) {
        const sceneData = response.scene.state_data;
        setSceneInfo({
          sequence: sceneData.scene_sequence,
          title: sceneData.scene_title,
          activeCharacterIds: sceneData.active_character_ids || [],
          setting: sceneData.setting_description,
          mood: sceneData.mood
        });
        console.log('✅ Scene info loaded:', sceneData.scene_sequence);
      } else {
        console.log('⚠️ No scene ready yet:', response.message);
        // Preserve existing sceneInfo — don't null it out during a transition.
        // The WebSocket handler updates sceneInfo when the new scene's Narrator
        // message arrives with update_scene_indicator: true.
      }
    } catch (error) {
      console.error('❌ Error loading scene info:', error);
    } finally {
      // Only clear loading if we actually set it
      if (hasSetLoading) {
        setSceneLoading(false);
      }
    }
  }, [room?.id]);

  // Helper functions for log filtering and parsing
  const parseLogEntry = useCallback((logLine) => {
    if (!logLine || typeof logLine !== 'string') return null;
    
    // Extract category from log line (look for emoji + category name)
    let category = 'OTHER';
    if (logLine.includes('🎯 DIRECTOR')) category = 'DIRECTOR';
    else if (logLine.includes('🎭 ACTOR')) category = 'ACTOR';
    else if (logLine.includes('📝 SCREENWRITER')) category = 'SCREENWRITER';
    else if (logLine.includes('🔧 INFRA')) category = 'INFRA';
    else if (logLine.includes('⚡ PERF')) category = 'PERF';
    else if (logLine.includes('🔴 ERROR')) category = 'ERROR';
    else if (logLine.includes('📋 USER_MESSAGE')) category = 'USER';
    
    return {
      content: logLine,
      category: category,
      timestamp: logLine.match(/\[(.*?)\]/)?.[1] || '',
      isError: logLine.includes('ERROR') || logLine.includes('FAILED'),
      isWarning: logLine.includes('WARN') || logLine.includes('⚠️')
    };
  }, []);

  const parseDetailedLogEntry = useCallback((logLines, startIndex) => {
    const mainLine = logLines[startIndex];
    const parsed = parseLogEntry(mainLine);
    if (!parsed) return null;

    // Look for "Data:" section in subsequent lines
    let detailLines = [];
    let i = startIndex + 1;
    let inDataSection = false;
    
    while (i < logLines.length) {
      const line = logLines[i];
      if (line.trim().startsWith('Data:')) {
        inDataSection = true;
        detailLines.push(line);
      } else if (inDataSection && (line.trim().startsWith('{') || line.trim().includes('"') || line.trim().includes('}') || line.trim() === '')) {
        detailLines.push(line);
        if (line.trim() === '}' && detailLines.filter(l => l.includes('{')).length === detailLines.filter(l => l.includes('}')).length) {
          break; // End of JSON data
        }
      } else if (line.trim().match(/^\[.*?\]/)) {
        break; // Start of next log entry
      } else if (inDataSection) {
        detailLines.push(line);
      }
      i++;
    }

    return {
      ...parsed,
      mainLine: mainLine,
      detailLines: detailLines,
      hasDetails: detailLines.length > 0,
      entryIndex: startIndex
    };
  }, [parseLogEntry]);

  const toggleLogExpansion = useCallback((entryIndex) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryIndex)) {
        newSet.delete(entryIndex);
      } else {
        newSet.add(entryIndex);
      }
      return newSet;
    });
  }, []);

  const getCategoryCount = useCallback((category) => {
    if (category === 'ALL') return storyLogs.length;
    return storyLogs.filter(log => {
      const parsed = parseLogEntry(log);
      return parsed && parsed.category === category;
    }).length;
  }, [storyLogs, parseLogEntry]);

  const getFilteredLogs = useCallback(() => {
    if (!storyLogs || storyLogs.length === 0) return [];
    
    // Parse all log entries with their details
    const detailedEntries = [];
    let i = 0;
    while (i < storyLogs.length) {
      const entry = parseDetailedLogEntry(storyLogs, i);
      if (entry) {
        detailedEntries.push(entry);
        // Skip the detail lines we've already processed
        i += (entry.detailLines.length > 0 ? entry.detailLines.length + 1 : 1);
      } else {
        i++;
      }
    }
    
    return detailedEntries.filter(entry => {
      // Filter by category
      if (logFilter !== 'ALL' && entry.category !== logFilter) {
        return false;
      }
      
      // Filter by search term
      if (searchTerm && !entry.content.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  }, [storyLogs, logFilter, searchTerm, parseDetailedLogEntry]);

  // Story log streaming effect
  useEffect(() => {
    if (!showStoryLog) return;

    const startLogStream = () => {
      try {
        // Use centralized config instead of direct environment variable
        const backendUrl = BACKEND_URL;
        console.log('🔗 Starting story log stream...', { backendUrl, showStoryLog });
        
        // First, load recent logs to show initial data
        const loadRecentLogs = async () => {
          try {
            const response = await fetch(`${backendUrl}/api/story/logs/recent?lines=20&room_id=${room.id}`);
            const data = await response.json();
            if (data.success && data.logs) {
              console.log('📚 Loaded recent logs:', data.logs.length);
              setStoryLogs(data.logs);
            }
          } catch (error) {
            console.warn('⚠️ Failed to load recent logs:', error);
          }
        };
        
        loadRecentLogs();
        
        const eventSource = new EventSource(`${backendUrl}/api/story/logs/stream?room_id=${room.id}`);
        
        eventSource.onopen = () => {
          console.log('✅ Story log stream connected');
          setStoryLogError(null);
        };
        
        eventSource.onmessage = (event) => {
          console.log('📨 Story log received:', event.data);
          const logLine = event.data;
          if (logLine && logLine.trim()) {
            setStoryLogs(prev => {
              const newLogs = [...prev, logLine];
              console.log('📝 Story logs updated, total:', newLogs.length);
              // Keep only last 500 lines to prevent memory issues
              return newLogs.slice(-500);
            });
          }
        };
        
        eventSource.onerror = (error) => {
          console.warn('🚨 Story log stream error:', error);
          console.log('EventSource state:', eventSource.readyState);
          setStoryLogError('Connection error - please refresh');
        };
        
        logStreamRef.current = eventSource;
        
      } catch (error) {
        console.error('❌ Failed to start story log stream:', error);
      }
    };

    startLogStream();

    // Cleanup log stream on component unmount or showStoryLog change
    return () => {
      if (logStreamRef.current) {
        console.log('🧹 Cleaning up story log stream...');
        logStreamRef.current.close();
        logStreamRef.current = null;
      }
    };
  }, [showStoryLog]);

  // Auto-scroll story logs to bottom
  const storyLogContainerRef = useRef(null);
  useEffect(() => {
    if (showStoryLog && storyLogContainerRef.current) {
      storyLogContainerRef.current.scrollTop = storyLogContainerRef.current.scrollHeight;
    }
  }, [storyLogs, showStoryLog]);
  useEffect(() => {
    loadSceneInfo();
  }, [loadSceneInfo]);

  // Refresh scene info when new messages arrive (to catch scene transitions)
  // Only refresh when director initiates actual scene transitions
  const lastMessageCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > 0) {
      // Check if the latest messages contain director scene transition messages
      const newMessages = messages.slice(lastMessageCountRef.current);
      const hasSceneTransition = newMessages.some(msg => 
        msg.message_type === 'scene_transition' || 
        msg.message_type === 'scene_change'
      );
      
      // Narrator opening beat arrived — clear the transitioning indicator
      if (hasSceneTransition) {
        setSceneTransitioning(false);
      }

      // Only refresh scene info on initial load or when director initiates scene transition
      if (lastMessageCountRef.current === 0 || hasSceneTransition) {
        console.log('🎬 Scene transition detected, refreshing scene info');
        loadSceneInfo().then(() => {
          lastMessageCountRef.current = messages.length;
        });
      } else {
        // Update reference without refreshing scene info
        lastMessageCountRef.current = messages.length;
      }
    }
  }, [messages.length, loadSceneInfo, setSceneTransitioning]);

  // Set up WebSocket connection and message handling - run once on mount
  useEffect(() => {
    debugWS('🚨 ChatRoom useEffect MOUNT - setting up handlers');
    debugWS('📊 Current handler count before cleanup:', getHandlerCount());
    
    // Clear ALL handlers to prevent duplicates (more robust than trying to match function references)
    clearAllHandlers();
    debugWS('📊 Handler count after clearing all:', getHandlerCount());
    
    // Add our message handler
    addMessageHandler(handleWebSocketMessage);
    handlerRegisteredRef.current = true;
    debugWS('✅ Message handler registered (with full cleanup)');
    debugWS('📊 Final handler count:', getHandlerCount());
    
    return () => {
      debugWS('💀 ChatRoom useEffect UNMOUNT - cleaning up handlers');
      if (handlerRegisteredRef.current) {
        clearAllHandlers(); // Clear all handlers on unmount
        handlerRegisteredRef.current = false;
        seenMessagesRef.current.clear();
        // Reset initial connection flag on unmount
        initialConnectionRef.current = true;
        // Disconnect WebSocket on component unmount
        disconnect();
        debugWS('✅ All message handlers cleaned up and WebSocket disconnected');
      }
    };
  }, []); // NO DEPENDENCIES - run only once
  
  // Separate effect to handle connection when dependencies change
  useEffect(() => {
    const timestamp = new Date().toISOString().substr(11, 12);
    debugWS(`🔌 [${timestamp}] CONNECTION EFFECT TRIGGERED:`, {
      hasRoom: !!room?.id,
      hasUser: !!user,
      hasSession: !!session?.access_token,
      roomId: room?.id,
      userId: user?.id,
      loadMessagesRef: typeof loadMessages
    });

    if (!room?.id) {
      debugWS(`❌ [${timestamp}] Missing room ID for WebSocket connection`);
      return;
    }

    // For guest users, we need different logic
    if (room.isGuest) {
      debugWS(`🔌 [${timestamp}] Guest user connection - checking stored guest data...`);
      
      // Get stored guest user data for WebSocket connection
      const storedGuestUser = sessionStorage.getItem('guest_user');
      if (storedGuestUser) {
        try {
          const guestUser = JSON.parse(storedGuestUser);
          const guestSession = { 
            access_token: `guest_${guestUser.id}`, 
            session_id: 'guest_session' 
          };
          
          debugWS(`🔌 [${timestamp}] Using stored guest user:`, guestUser.display_name);
          debugWS(`🔌 [${timestamp}] Guest token format:`, guestSession.access_token);
          
          // Reset initial connection flag when changing rooms/connections
          initialConnectionRef.current = true;
          
          // Load messages first
          loadMessages();
          
          // Connect to WebSocket with guest user data
          connectToWebSocket(room.id, guestUser, guestSession);
          
          // Update connection status
          setConnected(isConnected());
          
          return () => {
            debugWS(`🧹 [${timestamp}] GUEST CONNECTION EFFECT CLEANUP - Room ${room?.id}`);
            // Always disconnect when dependencies change to prevent multiple connections
            disconnect();
          };
        } catch (error) {
          debugWS(`❌ [${timestamp}] Error parsing stored guest user data:`, error);
        }
      } else {
        debugWS(`❌ [${timestamp}] No stored guest user data found`);
      }
      return;
    }

    // For authenticated users, check normal requirements
    if (!user || !session?.access_token) {
      debugWS(`❌ [${timestamp}] Missing dependencies for authenticated WebSocket connection`);
      return;
    }

    debugWS(`🔌 [${timestamp}] All dependencies available, proceeding with connection...`);
    
    // Reset initial connection flag when changing rooms/connections
    initialConnectionRef.current = true;
    
    // Load messages first
    loadMessages();
    
    // Connect to WebSocket - the connectToWebSocket function now handles duplicate calls gracefully
    connectToWebSocket(room.id, user, session);
    
    // Update connection status
    setConnected(isConnected());
    
    // Cleanup function to prevent connection leaks
    return () => {
      debugWS(`🧹 [${timestamp}] CONNECTION EFFECT CLEANUP - Room ${room?.id}`);
      // Always disconnect when dependencies change to prevent multiple connections
      disconnect();
    };
    
  }, [room?.id, user?.id, session?.access_token]); // Remove loadMessages dependency

  // Update connection status periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const wsInfo = getConnectionInfo();
      setConnected(wsInfo.connected);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);







  // Memoized send message function to prevent recreation - WITH SECURITY VALIDATION
  const sendMessage = useCallback(async (content) => {
    // debugLog('💬 sendMessage called', {
    //   contentLength: content?.length,
    //   hasContent: !!content?.trim(),
    //   connected,
    //   sending
    // });

    // Client-side validation for message content
    if (!content || !content.trim()) {
      // debugLog('⚠️ Empty message content, skipping send');
      setError('Message cannot be empty');
      return;
    }

    // Length validation (must match backend limits)
    if (content.length > 2000) {
      // debugLog('❌ Message too long', { length: content.length });
      setError('Message too long (maximum 2000 characters)');
      return;
    }

    // Line break validation (prevent spam)
    if (content.split('\n').length > 50) {
      // debugLog('❌ Too many line breaks', { lineCount: content.split('\n').length });
      setError('Too many line breaks in message');
      return;
    }

    if (sending) {
      // debugLog('⚠️ Already sending message, skipping duplicate send');
      return;
    }

    try {
      setSending(true);
      setError(''); // Clear any previous errors
      // debugLog('💬 Starting message send process via WebSocket utility');

      // OPTIMISTIC UPDATE: Add user's message to UI immediately
      const optimisticMessage = {
        id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for optimistic update
        content: content,
        sender_name: userDisplayName,
        message_type: 'user',
        created_at: new Date().toISOString(),
        room_id: room?.id,
        isOptimistic: true // Mark as optimistic for potential removal if send fails
      };

      console.log('📤 OPTIMISTIC UPDATE: Adding user message immediately', optimisticMessage);
      
      // Add to messages state immediately
      setMessages(prev => [...prev, optimisticMessage]);

      // 🔧 STEP 1: Clear typing state immediately on message send (before WebSocket call)
      // This prevents the flash from buffered typing events
      setTypingActiveCount(0);
      setTypingIsAnyoneTyping(false);
      
      stopDraftTimer();
      const success = await wsSendMessage(content, api);
      
      if (success) {
        // debugLog('💬 Message sent successfully');
        
        // Stop typing indicator when message is sent successfully
        handleTypingMessageSend();
        
        setNewMessage('');
        
        // Auto-focus the textarea for seamless conversation flow
        // Use timeout to ensure focus happens after all state updates
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        }, 50);
        
        // DON'T remove isOptimistic flag here - let the broadcast message update handle it
        // This ensures the optimistic message stays available for display name syncing
        console.log('📤 WebSocket send success - keeping optimistic flag for broadcast update');
      } else {
        // debugLog('❌ Message send failed');
        
        // Remove the optimistic message if send failed
        setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
        setError('Failed to send message. Please try again.');
      }

    } catch (error) {
      // debugLog('❌ Error sending message', {
      //   error: error.message
      // });
      console.error('Error sending message:', error);
      
      // Enhanced error handling for different error types
      if (error.message.includes('rate limit')) {
        setError('Sending too fast. Please wait a moment before sending another message.');
      } else if (error.message.includes('Invalid message content')) {
        setError('Message contains invalid content. Please check your message and try again.');
      } else if (error.message.includes('too long')) {
        setError('Message is too long. Please shorten your message and try again.');
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setSending(false);
      // debugLog('💬 Send message process completed');
    }
  }, [connected, sending, userDisplayName, room?.id, stopDraftTimer]); // Add userDisplayName for optimistic updates

  // Memoized send button handler to prevent recreation
  const handleSendClick = useCallback(() => {
    // debugLog('🖱️ Send button clicked');
    sendMessage(newMessage);
  }, [sendMessage, newMessage]); // Remove debugLog dependency

  // Handle Enter key press to send message
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey && !sceneTransitioning) {
      e.preventDefault();
      handleSendClick();
    }
  }, [handleSendClick, sceneTransitioning]);

  const generateGuestAIResponse = (userMessage) => {
    console.log("🤖 GUEST AI: Generating response");
    console.log("   User message:", userMessage);
    console.log("   Room narrative:", room.narrative);
    
    // Get narrative info for AI response
    const narrative = room.narrative;
    const genre = narrative?.genre?.toLowerCase() || 'adventure';
    let featuredCharacter = narrative?.featured_character || 'AI Character';
    
    // Backend now returns proper character names, no UUID mapping needed
    console.log("🎭 GUEST AI: Using character name from backend:", featuredCharacter);
    
    console.log("🎭 GUEST AI: Final character and genre");
    console.log("   Featured character:", featuredCharacter);
    console.log("   Genre:", genre);
    
    // =====================================================================
    // THE COMMENTED CODE BELOW SHOULD BE DELETED IN THE NEXT GIT PUSH
    // =====================================================================
    // HARDCODED RESPONSE SYSTEM COMPLETELY DISABLED - NO FALLBACKS ALLOWED
    // All AI responses must come from backend OpenAI system
    // If backend fails, let it fail - no more hardcoded generic responses
    // =====================================================================
    
    /*
    // Response templates based on genre
    const responseTemplates = {
      adventure: [
        "The shadows deepen around us. We need to be careful here.",
        "I hear something moving in the darkness ahead. Stay close.",
        "Look for any inscriptions on the walls - they might give us a clue.",
        "That sound... we're not alone in here. Keep your weapon ready.",
        "The air is getting thinner. We must be getting deeper underground."
      ],
      romance: [
        "How perfectly delightful! Though I must say, such directness is quite refreshing.",
        "Indeed, the evening has taken a most agreeable turn, would you not agree?",
        "Your words are most kind. I confess, I find your company quite agreeable.",
        "The music tonight has been exceptional, though I find the conversation even more engaging."
      ],
      'sci-fi': [
        "Fascinating. The readings I'm getting are unlike anything in our database.",
        "Captain, I'm detecting unusual energy signatures from that sector.",
        "The probability of success is... optimistic, but not impossible.",
        "Logic suggests we proceed with extreme caution here."
      ],
      mystery: [
        "Curious. Very curious indeed. The evidence suggests something quite different.",
        "Elementary! Though I must admit, this case has some intriguing twists.",
        "Notice how the dust has been disturbed here, but not there. Most revealing.",
        "The timeline doesn't quite fit, does it? Someone isn't telling the truth."
      ],
      western: [
        "Stranger, this ain't the safest place to be after dark. Just sayin'.",
        "I've seen men draw faster, but not many who lived to tell about it.",
        "The desert has a way of revealing a man's true character.",
        "Word of advice: keep your hand away from your iron unless you plan to use it."
      ],
      fantasy: [
        "The magic here is ancient and powerful. We must tread carefully.",
        "I sense great darkness ahead, but also the possibility of light.",
        "The old prophecies spoke of such times as these. We must have courage.",
        "Even the smallest person can change the course of the future."
      ],
      horror: [
        "Did you hear that? We should leave. Now.",
        "Something's not right here. The very air feels... wrong.",
        "The shadows seem to be moving on their own. Stay in the light.",
        "Every instinct I have is screaming at me to run. Maybe we should listen."
      ],
      comedy: [
        "Well, that went about as well as expected. Which is to say, terribly.",
        "Maximum effort! Though admittedly, my maximum might be questionable.",
        "I have a plan. It's not a good plan, but it's definitely a plan.",
        "This is either going to be epic or epically stupid. Possibly both."
      ]
    };
    */
    
    // Character-specific responses
    /*
    // Character-specific responses
    const characterResponses = {
      "Indiana Jones": [
        "Fortune favors the bold. Let's see what we're dealing with here.",
        "I've got a bad feeling about this, but we press on.",
        "Trust me, I've seen stranger things in my time.",
        "Sometimes you've got to improvise. Follow my lead."
      ],
      "Sherlock Holmes": [
        "Elementary observation reveals much about our current predicament.",
        "The game is afoot, and the pieces are falling into place.",
        "Curious... very curious indeed. The evidence suggests otherwise.",
        "I observe everything. The details others miss tell the real story."
      ],
      "Deadpool": [
        "Maximum effort! Though my maximum might be questionable.",
        "I'm touching myself tonight. Wait, wrong context. Probably.",
        "Life is an endless series of trainwrecks with brief commercial breaks.",
        "Fourth wall? What fourth wall? We're having a conversation here!"
      ]
    };
    */
    

    // FALLBACK SYSTEM DISABLED - NO MORE HARDCODED RESPONSES
    // If backend/AI fails, let it fail - guest users should see clear error messages
    console.log("❌ GUEST AI: Hardcoded fallback system disabled - no AI response generated");
    console.log("   Backend AI system should handle all responses");
    
    // Could add a system message here explaining AI is temporarily unavailable
    const errorMessage = {
      id: `system-${Date.now()}`,
      content: "AI character temporarily unavailable. Please try again in a moment.",
      sender_name: "System",
      sender_type: 'system',
      timestamp: new Date().toISOString(),
      room_id: room.id
    };
    
    setTimeout(() => {
      if (isMountedRef.current) {
        setMessages(prevMessages => [...prevMessages, errorMessage]);
      }
    }, 1000);
  };


  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="h-screen bg-gray-900 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700 p-4 flex-shrink-0">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={onLeave}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition duration-200"
            >
              ← Leave Room
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">{room.name}</h1>
              <p className="text-gray-300 text-sm">
                {room.narrative?.title}{room.narrative?.genre ? ` • ${room.narrative.genre}` : ''}
                {room.room_code && (
                  <span className="ml-2 bg-blue-600 px-2 py-1 rounded text-xs">
                    Code: {room.room_code}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Invite Button */}
            <InviteButton isAuthenticated={!!user} onSignIn={onSignIn} roomId={room?.id} />
            
            {/* Connection Status Indicator */}
            {user ? (
              <div className="flex items-center space-x-2 text-sm">
                {connected ? (
                  <div className="flex items-center text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    Connected
                  </div>
                ) : (
                  <div className="flex items-center text-red-600">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                    Disconnected
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <span className="text-yellow-400 text-sm">● Guest Mode</span>
                <button
                  onClick={onSignIn}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scene Information with Toggle Button */}
      <div className="bg-gray-800 border-b border-gray-700 p-3 flex-shrink-0">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {sceneLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-400 text-sm">Loading scene...</span>
                </div>
              ) : sceneInfo ? (
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-600 px-2 py-1 rounded">
                    <span className="text-white text-sm">Scene {sceneInfo.sequence}</span>
                  </div>
                  <span className="text-gray-300 text-sm">
                    {sceneInfo.activeCharacterIds.length} active character{sceneInfo.activeCharacterIds.length !== 1 ? 's' : ''}
                  </span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-yellow-400 text-sm">Scene initializing...</span>
                </div>
              )}
            </div>
            
            {/* Toggle Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowStoryLog(!showStoryLog)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  showStoryLog
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-600 text-gray-200 hover:bg-gray-700'
                }`}
              >
                {showStoryLog ? 'StoryLog' : 'Scene'}
              </button>
              <AdminOnly>
                <button
                  onClick={() => setShowRoomEditor(!showRoomEditor)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    showRoomEditor
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-600 text-gray-200 hover:bg-gray-700'
                  }`}
                >
                  RoomEditor
                </button>
              </AdminOnly>
            </div>
          </div>
        </div>
      </div>

      {/* Story Context or Story Log Viewer */}
      {room.narrative && (
        <div className="bg-gray-800 border-b border-gray-700 p-4 flex-shrink-0">
          <div className="max-w-6xl mx-auto">
            {showStoryLog ? (
              /* Enhanced Live Story Log Viewer */
              <div className="bg-gray-900 rounded-lg p-4 h-64">
                {/* Header with Title and Controls */}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-green-400">Story Orchestration Log</h3>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={async () => {
                        try {
                          const backendUrl = BACKEND_URL;
                          console.log('🧪 Creating test logs...', backendUrl);
                          const response = await fetch(`${backendUrl}/api/story/logs/test`, { method: 'POST' });
                          const result = await response.json();
                          console.log('🧪 Test logs created:', result);
                        } catch (error) {
                          console.error('❌ Failed to create test logs:', error);
                        }
                      }}
                      className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded"
                    >
                      Test
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const backendUrl = BACKEND_URL;
                          console.log('🔄 Refreshing logs...', backendUrl);
                          const response = await fetch(`${backendUrl}/api/story/logs/recent?lines=50&room_id=${room.id}`);
                          const data = await response.json();
                          if (data.success && data.logs) {
                            console.log('📚 Refreshed logs:', data.logs.length);
                            setStoryLogs(data.logs);
                          }
                        } catch (error) {
                          console.error('❌ Failed to refresh logs:', error);
                        }
                      }}
                      className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
                    >
                      Refresh
                    </button>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-green-400 text-xs">LIVE</span>
                    </div>
                  </div>
                </div>

                {/* Filter Buttons */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {['ALL', 'DIRECTOR', 'ACTOR', 'SCREENWRITER', 'PERF', 'ERROR', 'USER'].map(filter => (
                    <button
                      key={filter}
                      onClick={() => {
                        setLogFilter(filter);
                        setExpandedLogs(new Set()); // Clear expanded state when filter changes
                      }}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                        logFilter === filter
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {filter === 'DIRECTOR' && '🎯 '}
                      {filter === 'ACTOR' && '🎭 '}
                      {filter === 'SCREENWRITER' && '📝 '}
                      {filter === 'PERF' && '⚡ '}
                      {filter === 'ERROR' && '🔴 '}
                      {filter === 'USER' && '👤 '}
                      {filter}
                      <span className="ml-1 text-xs opacity-75">({getCategoryCount(filter)})</span>
                    </button>
                  ))}
                </div>

                {/* Search Box */}
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search logs... (room ID, operation, character, etc.)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1 text-sm bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div 
                  ref={storyLogContainerRef}
                  className="bg-black rounded border border-gray-700 h-48 overflow-y-auto p-3 font-mono text-xs"
                  style={{ scrollBehavior: 'smooth' }}
                >
                  {storyLogs.length === 0 ? (
                    <div className="text-gray-500 text-center py-8">
                      Waiting for story orchestration events...
                      <br />
                      <span className="text-xs">Send a message to trigger story processing</span>
                      <br />
                      <span className="text-xs text-blue-400">Connection: {logStreamRef.current ? 'Connected' : 'Connecting...'}</span>
                    </div>
                  ) : (
                    <>
                      <div className="text-green-400 text-xs mb-2">
                        📡 Streaming {storyLogs.length} total entries 
                        {logFilter !== 'ALL' && ` | Showing ${getFilteredLogs().length} ${logFilter} entries`}
                        {searchTerm && ` | Search: "${searchTerm}"`}
                        {expandedLogs.size > 0 && ` | ${expandedLogs.size} expanded`}
                      </div>
                      {getFilteredLogs().length === 0 && (logFilter !== 'ALL' || searchTerm) ? (
                        <div className="text-yellow-400 text-center py-4">
                          No entries match current filter{searchTerm && ' and search'}
                          <br />
                          <span className="text-xs">Try different filter or search term</span>
                        </div>
                      ) : (
                        getFilteredLogs().map((entry, index) => (
                          <div key={`${entry.entryIndex}-${index}`} className="mb-2">
                            {/* Main log line - clickable */}
                            <div 
                              onClick={() => entry.hasDetails && toggleLogExpansion(entry.entryIndex)}
                              className={`cursor-pointer select-none ${
                                entry.isError ? 'text-red-400' :
                                entry.isWarning ? 'text-yellow-400' :
                                entry.category === 'DIRECTOR' ? 'text-blue-400' :
                                entry.category === 'ACTOR' ? 'text-purple-400' :
                                entry.category === 'SCREENWRITER' ? 'text-green-400' :
                                entry.category === 'PERF' ? 'text-yellow-300' :
                                entry.category === 'INFRA' ? 'text-gray-400' :
                                entry.category === 'USER' ? 'text-cyan-400' :
                                'text-gray-300'
                              } ${entry.hasDetails ? 'hover:bg-gray-800 hover:bg-opacity-50 rounded px-1' : ''}`}
                            >
                              {entry.hasDetails && (
                                <span className="inline-block w-3 text-gray-500 text-xs mr-1">
                                  {expandedLogs.has(entry.entryIndex) ? '▼' : '▶'}
                                </span>
                              )}
                              {entry.hasDetails ? (
                                <span className="border-b border-dotted border-gray-600">{entry.mainLine}</span>
                              ) : (
                                entry.mainLine
                              )}
                              {entry.hasDetails && (
                                <span className="ml-2 text-xs text-gray-500 italic">
                                  {expandedLogs.has(entry.entryIndex) ? 'click to collapse' : 'click for details'}
                                </span>
                              )}
                            </div>
                            
                            {/* Detailed data section - expandable */}
                            {entry.hasDetails && expandedLogs.has(entry.entryIndex) && (
                              <div className="ml-4 mt-1 p-2 bg-gray-800 bg-opacity-50 rounded text-xs text-gray-400 border-l-2 border-gray-600">
                                {entry.detailLines.map((detailLine, detailIndex) => (
                                  <div key={detailIndex} className="font-mono">
                                    {detailLine}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* Original Story Context */
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-300 mb-2">Story Context</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {room.narrative.description}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Story Room Editor Panel */}
      {showRoomEditor && (
        <AdminOnly>
          <div className="bg-gray-800 border-b border-gray-700 p-4 flex-shrink-0 overflow-y-auto max-h-96">
            <div className="max-w-6xl mx-auto">
              <StoryRoomEditor room={room} />
            </div>
          </div>
        </AdminOnly>
      )}

      {/* Messages Container - Fixed Height with Scroll */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="max-w-6xl mx-auto w-full h-full flex flex-col p-4">
          {/* Messages Area - Scrollable */}
          <div className="flex-1 bg-gray-800 rounded-lg border border-gray-700 p-4 mb-4 overflow-y-auto min-h-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-gray-400">Loading messages...</div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-center text-gray-400">
                  <p className="text-lg">Welcome to the story!</p>
                  <p className="text-sm mt-2">Start the conversation and let the adventure begin...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => {
                  // Check if this message is from the current user
                  let isCurrentUser = false;
                  
                  if (user) {
                    // Authenticated user - compare sender name with user display name
                    isCurrentUser = message.sender_name === userDisplayName;
                  } else {
                    // Guest user - get stored guest user data to compare
                    const storedGuestUser = sessionStorage.getItem('guest_user');
                    
                    if (storedGuestUser) {
                      try {
                        const guestUser = JSON.parse(storedGuestUser);
                        
                        // FIXED GUEST MESSAGE OWNERSHIP: Use exact matching only, no patterns
                        isCurrentUser = message.sender_name === guestUser.display_name;
                        
                      } catch (error) {
                        console.error('🎭 GUEST USER DEBUG: Error parsing guest user data:', error);
                      }
                    } else {
                      console.warn('No stored guest user data found for message comparison');
                      // Fallback: no precise match possible
                      isCurrentUser = false;
                    }
                  }
                  
                  return (
                    <div
                      key={message.id || `msg-${message.created_at || Date.now()}-${Math.random()}`}
                      className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md xl:max-w-lg px-4 py-2 rounded-lg ${
                          message.message_type === 'ai'
                            ? 'bg-gray-700 text-white' // AI characters - grey to match palette
                            : isCurrentUser
                            ? 'bg-blue-600 text-white' // Current user
                            : 'bg-gray-600 text-white'  // Other users (including narrator)
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium opacity-75">
                            {message.sender_name || 'Unknown'}
                          </span>
                          <span className="text-xs opacity-50">
                            {formatTime(message.created_at)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>



          {/* Message Input - Fixed at Bottom */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 flex-shrink-0">
            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded mb-3 text-sm">
                {error}
              </div>
            )}
            
            {/* Character Counter */}
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-400">
                {newMessage.length}/2000 characters
              </span>
              {newMessage.length > 1800 && (
                <span className="text-xs text-yellow-400">
                  {newMessage.length > 1950 ? 'Character limit almost reached!' : 'Approaching character limit'}
                </span>
              )}
            </div>
            
        {responseDraftsAvailable && <label className="block text-xs text-gray-300 mb-2">
          <input type="checkbox" checked={responseDraftsEnabled}
            onChange={event => setResponseDraftsEnabled(event.target.checked)} />{' '}
          Prepare replies while I type
          <span className="block">AI processes this draft before Send. Unsent text is temporary and is not posted or saved.</span>
        </label>}
            <div className="flex space-x-2">
              <div className="relative flex-1">
                <textarea
                  ref={textareaRef}
                  className={`w-full p-3 border rounded-l-lg focus:outline-none focus:ring-2 resize-none bg-gray-800 text-gray-100 placeholder-gray-400 ${
                    newMessage.length > 2000 
                      ? 'border-red-500 focus:ring-red-500' 
                      : 'border-gray-600 focus:ring-blue-500'
                  }`}
                  placeholder={
                    sceneTransitioning
                      ? ''
                      : (!newMessage || newMessage.trim().length === 0) && !isCurrentUserTyping && typingIsAnyoneTyping
                        ? '' // Empty placeholder when showing custom typing indicator
                        : 'Type your message...'
                  }
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onKeyDown={handleTypingKeyDown}
                  disabled={sending || sceneTransitioning}
                  rows="1"
                  style={{
                    minHeight: '50px',
                    maxHeight: '120px'
                  }}
                />
                {/* Narrator is writing indicator — shown during scene transitions */}
                {sceneTransitioning && (
                  <div className="absolute inset-3 pointer-events-none text-gray-400" style={{
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    lineHeight: 'inherit'
                  }}>
                    <span>Narrator is writing</span>
                    <span className="typing-dots ml-1"><span className="dot bouncing-dot dot-1">.</span><span className="dot bouncing-dot dot-2">.</span><span className="dot bouncing-dot dot-3">.</span></span>
                  </div>
                )}
                {/* Custom typing indicator that matches textarea placeholder exactly */}
                {!sceneTransitioning && (!newMessage || newMessage.trim().length === 0) && !isCurrentUserTyping && typingIsAnyoneTyping && (
                  <div className="absolute inset-3 pointer-events-none text-gray-400" style={{
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    lineHeight: 'inherit'
                  }}>
                    <span>Someone is typing</span>
                    <span className="typing-dots ml-1"><span className="dot bouncing-dot dot-1">.</span><span className="dot bouncing-dot dot-2">.</span><span className="dot bouncing-dot dot-3">.</span></span>
                    <span className="ml-1">({typingActiveCount})</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleSendClick}
                disabled={sceneTransitioning || sending || !connected || !newMessage.trim() || newMessage.length > 2000}
                className={`px-6 py-3 rounded-r-lg font-medium transition-colors ${
                  sceneTransitioning || sending || !connected || !newMessage.trim() || newMessage.length > 2000
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced memoization comparison - only re-render when critical props actually change
const arePropsEqual = (prevProps, nextProps) => {
  // Room comparison - check if it's the same room with same narrative
  const roomChanged = (
    prevProps.room?.id !== nextProps.room?.id ||
    prevProps.room?.narrative?.id !== nextProps.room?.narrative?.id ||
    prevProps.room?.isGuest !== nextProps.room?.isGuest ||
    prevProps.room?.guest_user_id !== nextProps.room?.guest_user_id
  );
  
  // User comparison - check if user identity changed  
  const userChanged = (
    prevProps.user?.id !== nextProps.user?.id ||
    prevProps.user?.username !== nextProps.user?.username
  );
  
  // OnLeave comparison - check function reference
  const onLeaveChanged = prevProps.onLeave !== nextProps.onLeave;
  
  console.log('🔍 ChatRoom.arePropsEqual comparison:', {
    roomChanged,
    userChanged, 
    onLeaveChanged,
    shouldRerender: roomChanged || userChanged || onLeaveChanged
  });
  
  // Return true if props are equal (no re-render needed)
  return !roomChanged && !userChanged && !onLeaveChanged;
};

ChatRoom.displayName = 'ChatRoom';

export default memo(ChatRoom, arePropsEqual);
